import { ReactNode, useEffect, useMemo, useState } from "react";
import styles from "./style.module.scss";
import PrintPageHeader from "@components/print/PrintElements/PrintPageHeader";
import PrintDocumentHeader from "@components/print/PrintElements/PrintDocumentHeader";
import { formatDate } from "@utils/Api";
import { chunkArray } from "@utils/Utils";
import { SuscriptionDataType } from "@/api/SuscriptionsApi";
import cx from "classnames";
import FooterPageNumber from "@components/print/PrintElements/PrintPageFooter/FooterPageNumber";
import { formatMoneyValue } from "@utils/Currency";
import { CURRENCIES_CODE_IDS } from "@utils/Constants";
import { ReportTotalDetail } from "@utils/DocumentStatus";
import LoadingScreen from "@components/content/LoadingScreen";
import { buildReportWorkbookBytes, ExcelHeading, REPORT_EXCEL_CONTENT_TYPE } from "@utils/ReportExcel";
import { EXPORT_ID_PDF_QUERY_PARAM } from "@components/print/PrintElements/PrintPage";
import { PrintPageDataLoadedPubSub, PrintPageDataLoadedPubSubPayload, ReportExcelExportedPubSub } from "@utils/pubsubs/PubSub";
import { useSearchParams } from "react-router-dom";

import { saveAs } from "file-saver";

export type ReportTitleProps = {
  label: string,
  width: string,
  hideFromPdf?: boolean,
  hideFromAll?: boolean,
  isNumeric?: boolean,
  className?: string
}

export type ReportDataProps<T = unknown> = {
  sourceData?: T,
  excludeFromTotal?: boolean,
  values: string[],
  className?: string
}

type ReportPageProps = {
  loading?: boolean,
  maxItemPerPage?: number,
  excelExport?: boolean,
  suscription: SuscriptionDataType,
  reportConfig: {
    title: string,
    xmlFileName?: string,
    date: string,
    subtitle?: string,
    onlyTotals?: boolean,
    /** Encabezado informativo que se escribe arriba de la tabla, solo en el Excel. */
    excelHeading?: ExcelHeading
  },
  reportTitles: ReportTitleProps[]
  reportData: ReportDataProps[]
  reportTotals?: ReportTotalDetail[]
}

const ReportPage = (props: ReportPageProps) => {
  const { maxItemPerPage = 45, suscription, reportConfig, reportData, reportTitles = [],
    reportTotals, loading = false, excelExport = false } = props;
  const [searchParams] = useSearchParams();
  /** Presente solo cuando la ruta se monta en un portal de exportacion (`renderRouteToExcel`). */
  const exportId = searchParams.get(EXPORT_ID_PDF_QUERY_PARAM);

  const [pages, setPages] = useState<ReportDataProps[][]>([])
  const [isReportExported, setReportExported] = useState<boolean>(false)
  /**
   * Los reportes avisan por PubSub cuando terminaron de cargar. Sin esa señal no se
   * puede distinguir "todavia no arranco la consulta" de "el periodo no tuvo
   * movimientos", y el Excel saldria con los datos a medio cargar.
   */
  const [isDataLoadCompleted, setDataLoadCompleted] = useState<boolean>(false)

  const reportHeaderSignature = useMemo(
    () => reportTitles.map((rt) => rt.label).join("\u001f"),
    [reportTitles]
  );

  useEffect(() => {
    const reportPages = chunkArray(reportData || [], maxItemPerPage)
    setPages(reportPages)
  }, [reportData])

  useEffect(() => {
    const unsubscribe = PrintPageDataLoadedPubSub.subscribe((payload: PrintPageDataLoadedPubSubPayload) => {
      setDataLoadCompleted(payload.dataLoaded || false);
    });
    return () => {
      unsubscribe();
    };
  }, [])

  /** Si cambian las cabeceras (p. ej. columnas IVA), hay que volver a exportar con el layout correcto. */
  useEffect(() => {
    setReportExported(false);
  }, [reportHeaderSignature, excelExport]);

  useEffect(() => {
    if (excelExport && !isReportExported && !loading && (reportData.length > 0 || isDataLoadCompleted)) {
      exportToExcel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excelExport, reportData, reportConfig, reportHeaderSignature, isReportExported, loading, isDataLoadCompleted]);

  const exportToExcel = () => {
    const bytes = buildReportWorkbookBytes(reportTitles, reportData, reportConfig.excelHeading);
    const fileName = `${reportConfig.xmlFileName || reportConfig.title}.xlsx`;

    setReportExported(true)

    if (exportId) {
      /** Dentro de un portal de exportacion no se descarga: los bytes viajan al paquete contable. */
      ReportExcelExportedPubSub.publish({ exportId, fileName, bytes });
      return;
    }

    const blob = new Blob([bytes], { type: REPORT_EXCEL_CONTENT_TYPE });
    saveAs(blob, fileName);
  };

  const addSpacerRows = (spacerRowCount: number): ReactNode[] => {
    const spacerRows = []

    for (let i = 0; i < spacerRowCount; i++) {
      spacerRows.push((
        <tr className={cx(["table-row spacer", styles.reportLine])}>
          {reportTitles.filter(rt => !rt.hideFromPdf && !rt.hideFromAll).map(t => <td>&nbsp;</td>)}
        </tr>
      ))
    }
    return spacerRows
  }

  const renderPageData = (entries: ReportDataProps[]): ReactNode[] => {
    return entries.map(e => {
      const values = e.values;
      return (
        <tr className={cx([e.className, styles.reportLine])}>
          {values.filter((v, i) => {
            const titleConfig = reportTitles[i];
            return !titleConfig?.hideFromPdf && !titleConfig?.hideFromAll
          }).map(v => {
            return (<td>
              {`${v}`}
            </td>)
          })}
        </tr>
      )
    })
  }

  const renderPage = (pageEntries: ReportDataProps[], index: number): ReactNode => {
    const spacerRowCount = maxItemPerPage - pageEntries.length;

    return (
      <>
        <PrintPageHeader suscription={suscription} />
        <div className={cx([styles.fullWidth])}>
          <PrintDocumentHeader
            title={reportConfig.title}
            subtitle={reportConfig.subtitle}
            fecha={formatDate(reportConfig.date, "YYYY-MM-DD", "DD.MM.YY")} />
          <div className="invoice-details">
            <table className="productos" cellSpacing="0">
              <thead>
                <tr className={styles.headingLine}>
                  {reportTitles.filter(rt => !rt.hideFromPdf && !rt.hideFromAll).map(t => {
                    return (<th style={{ width: `${t.width}` }}>{t.label}</th>)
                  })}
                </tr>
              </thead>
              <tbody>
                {renderPageData(pageEntries)}
                {addSpacerRows(spacerRowCount)}
              </tbody>
            </table>
            <FooterPageNumber
              currentPage={index + 1}
              totalPages={pages.length}
              forcePageBreak={reportTotals && reportTotals.length > 0}
            />
          </div>
        </div>
      </>
    )
  }

  const renderReportTotals = () => {
    return reportTotals && reportTotals.length > 0 && (
      <div>
        <PrintPageHeader suscription={suscription} />
        <div className={cx([styles.fullWidth])}>
          <PrintDocumentHeader
            title="Totales generales"
            subtitle={reportConfig.subtitle}
            fecha={formatDate(reportConfig.date, "YYYY-MM-DD", "DD.MM.YY")} />
          <div className="reports-totals">
            {reportTotals.map(rt => {
              return (
                <div className={styles.totalRow}>
                  <div className={cx({
                    [styles.totalLabel]: true,
                    [styles.separatorLine]: rt.separatorLine
                  })}>
                    {rt.title}
                  </div>
                  {rt.total != null && (<div className={styles.totalValue}>
                    {formatMoneyValue(rt.total, CURRENCIES_CODE_IDS[rt.currencyCode || "CRC"])}
                  </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div >
    )
  }

  if (excelExport) {
    return (
      <>
        <LoadingScreen isLoading={loading} />
        {!loading && <div style={{ padding: 16 }}>Descargando Excel…</div>}
      </>
    );
  }

  return (
    <div>
      {!loading && pages.length == 0 && (
        <h2 className="mt-5">No hay datos para generar un reporte</h2>
      )}
      {pages.map((p, index) => renderPage(p, index))}
      {renderReportTotals()}
      <LoadingScreen isLoading={loading} />
    </div>
  );
}

export default ReportPage;